import { MessageSquareIcon, MonitorIcon } from 'lucide-react'
import { type FC, memo, type ReactNode } from 'react'

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'

import { ChatAttachmentOption } from './chat-attachment-option.atom'
import { ChatInputAttachButtonDisabled } from './chat-input-attach-button-disabled.atom'
import { ChatInputAttachTrigger } from './chat-input-attach-trigger.atom'

export interface ChatInputAttachmentSheetProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onSelectFromComputer: () => void
  onSelectFromChats: () => void
  isDisabled?: boolean
  disabledReason?: string
  className?: string
}

const ChatInputAttachmentSheetBase: FC<ChatInputAttachmentSheetProps> = ({
  isOpen,
  onOpenChange,
  onSelectFromComputer,
  onSelectFromChats,
  isDisabled = false,
  disabledReason,
  className,
}): ReactNode => {
  if (disabledReason) {
    return <ChatInputAttachButtonDisabled reason={disabledReason} className={className} />
  }

  const createHandler = (callback: () => void) => (): void => {
    callback()
    onOpenChange(false)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetTrigger asChild>
        <ChatInputAttachTrigger isDisabled={isDisabled} className={className} />
      </SheetTrigger>
      <SheetContent side="bottom" className="chat-input-attachment-sheet rounded-t-2xl">
        <SheetHeader className="pb-2">
          <SheetTitle>Add attachment</SheetTitle>
        </SheetHeader>
        <div className="space-y-1 pb-4">
          <ChatAttachmentOption
            icon={MonitorIcon}
            label="Your computer"
            description="Upload files from your device"
            onClick={createHandler(onSelectFromComputer)}
          />
          <ChatAttachmentOption
            icon={MessageSquareIcon}
            label="Other chats"
            description="Attach from previous conversations"
            onClick={createHandler(onSelectFromChats)}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}

export const ChatInputAttachmentSheet = memo(ChatInputAttachmentSheetBase)

ChatInputAttachmentSheet.displayName = 'ChatInputAttachmentSheet'
